import React from "react";
import { API } from "@/lib/api";

/**
 * Round avatar with photo fallback to initials.
 * Handles absolute URLs and relative /api paths (auto-prefixes backend URL).
 */
export default function Avatar({
  src,
  name = "",
  size = "md",
  className = "",
  tone = "amber",
  objectPosition = "center 25%",
  ringed = false,
  testid,
}) {
  const sizeMap = {
    xs: "h-6 w-6 text-[10px]",
    sm: "h-8 w-8 text-xs",
    md: "h-10 w-10 text-sm",
    lg: "h-14 w-14 text-base",
    xl: "h-24 w-24 text-2xl",
  };
  const toneMap = {
    amber:   "bg-amber-100 text-amber-800",
    sky:     "bg-sky-100 text-sky-800",
    emerald: "bg-emerald-100 text-emerald-800",
    slate:   "bg-slate-100 text-slate-700",
  };

  const initials = (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("") || "?";

  const ringCls = ringed ? "ring-2 ring-white shadow-md" : "";

  if (src) {
    // Resolve relative /api paths to full backend URL so images load cross-origin correctly.
    const resolvedSrc = src.startsWith("/api/")
      ? `${API.replace(/\/api$/, "")}${src}`
      : src;
    return (
      <div
        className={`relative shrink-0 rounded-full overflow-hidden bg-slate-100 ${sizeMap[size]} ${ringCls} ${className}`}
        data-testid={testid}
      >
        <img
          src={resolvedSrc}
          alt={name || "avatar"}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition }}
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div
      className={`shrink-0 rounded-full flex items-center justify-center font-bold ${toneMap[tone]} ${sizeMap[size]} ${ringCls} ${className}`}
      data-testid={testid}
    >
      {initials}
    </div>
  );
}
